/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { useResize, animated, useSpring } from '@react-spring/web';
import { ArrowRight } from 'lucide-react';
import {
    FunctionComponent,
    MouseEvent,
    MutableRefObject,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { useSearchParams } from 'react-router';

import { StatusIcon } from '../atoms/StatusIcon';
import { Button } from '../ui/button';

import { OUTPUT } from '@/commons/data';
import { RunExecution, RunExecutionDetails } from '@/commons/protocols';
import { RunStatus } from '@/commons/protocols';
import { useUserContext } from '@/pages/Root';
import { cn } from '@/utils';

type ExecutionCardDetailsProps = {
    execution: RunExecutionDetails;
    projectSlug?: string;
    suiteSlug?: string;
    executionId?: string;
    socketCancelRun?: () => void;
    handleArchive: (id: number) => void;
    handleRun?: (id: number) => void;
    // New multi-select props
    selectedRuns?: { runId: number; executionId: number; storyId?: number }[];
    onSelectRun?: (run: RunExecution, event: MouseEvent<HTMLButtonElement>) => void;
    // Keep for backward compatibility
    selectedRun?: { runId: number; executionId: number };
    setSelectedRun?: (run: { runId: number; executionId: number }) => void;
    openStepViewer?: () => void;
};

const ExecutionCardDetails = ({
    execution,
    projectSlug,
    suiteSlug,
    executionId,
    selectedRuns = [],
    onSelectRun,
    selectedRun,
    setSelectedRun = () => {},
    socketCancelRun,
    handleArchive,
    handleRun,
    openStepViewer,
}: ExecutionCardDetailsProps) => {
    const [searchParams] = useSearchParams();
    const { canOperate } = useUserContext().user;

    if (!execution?.runs?.length) {
        return <div className="text-xs">No run yet</div>;
    }

    const runStatusPriority: Record<RunStatus, number> = {
        finished: 1,
        failed: 2,
        canceled: 3,
        running: 4,
        queued: 5,
    };

    const outputPriority: Record<OUTPUT, number> = {
        PENDING: 1,
        PASS: 2,
        FAIL: 3,
        DISMISSED: 4,
    };

    const sortedRuns = useMemo(() => {
        return execution.runs.sort((a, b) => {
            // First, prioritize enabled stories over disabled ones
            if (a.storyEnabled !== b.storyEnabled) {
                return a.storyEnabled === false ? 1 : -1;
            }

            // Compare by run status priority first
            const statusComparison = (runStatusPriority[a.status] || 99) - (runStatusPriority[b.status] || 99);
            if (statusComparison !== 0) return statusComparison;

            // If the status is the same, compare by output priority
            if (a.output && b.output) {
                const resultComparison = (outputPriority[a.output] || 99) - (outputPriority[b.output] || 99);
                if (resultComparison !== 0) return resultComparison;
            }

            // If both status and output are the same, sort by storyId
            return a.storyId - b.storyId;
        });
    }, [execution.runs]);

    useEffect(() => {
        if (!sortedRuns.length) return;
        if (!selectedRun && selectedRuns.length === 0 && executionId) {
            if (setSelectedRun) {
                setSelectedRun({ runId: sortedRuns[0].id, executionId: parseInt(executionId) });
            } else if (onSelectRun) {
                // Create a mock event since we're triggering this programmatically
                onSelectRun(sortedRuns[0], { shiftKey: false } as unknown as MouseEvent<HTMLButtonElement>);
            }
            return;
        }

        // Check if any currently selected run exists in the sorted runs
        const anyRunExists =
            selectedRuns.length > 0
                ? selectedRuns.some((selected) => sortedRuns.some((run) => run.id === selected.runId))
                : selectedRun
                  ? sortedRuns.some((run) => run.id === selectedRun.runId)
                  : false;

        if (!anyRunExists && executionId) {
            if (setSelectedRun) {
                setSelectedRun({ runId: sortedRuns[0].id, executionId: parseInt(executionId) });
            } else if (onSelectRun) {
                onSelectRun(sortedRuns[0], { shiftKey: false } as unknown as MouseEvent<HTMLButtonElement>);
            }
        }
    }, [selectedRun, selectedRuns, sortedRuns, executionId, setSelectedRun, onSelectRun]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            // Disable keyboard navigation when step viewer is open
            if (searchParams.get('stepViewer') === 'true') return;
            if (!execution?.runs?.length || !executionId) return;

            // Current selection reference point (use first selected run if multiple)
            const referenceRunId =
                selectedRuns.length > 0 ? selectedRuns[0].runId : selectedRun ? selectedRun.runId : undefined;

            if (!referenceRunId) return;

            const currentIndex = sortedRuns.findIndex((run) => run.id === referenceRunId);

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const nextIndex = currentIndex === sortedRuns.length - 1 ? 0 : currentIndex + 1;
                const nextRun = sortedRuns[nextIndex];

                // Use the new handler if available, otherwise fall back to legacy
                if (onSelectRun) {
                    onSelectRun(nextRun, { shiftKey: e.shiftKey } as unknown as MouseEvent<HTMLButtonElement>);
                } else if (setSelectedRun) {
                    setSelectedRun({ runId: nextRun.id, executionId: parseInt(executionId) });
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prevIndex = currentIndex <= 0 ? sortedRuns.length - 1 : currentIndex - 1;
                const prevRun = sortedRuns[prevIndex];

                // Use the new handler if available, otherwise fall back to legacy
                if (onSelectRun) {
                    onSelectRun(prevRun, { shiftKey: e.shiftKey } as unknown as MouseEvent<HTMLButtonElement>);
                } else if (setSelectedRun) {
                    setSelectedRun({ runId: prevRun.id, executionId: parseInt(executionId) });
                }
            } else if (e.key === 'Enter' && (selectedRun || selectedRuns.length === 1)) {
                e.preventDefault();
                openStepViewer?.();
            }
        },
        [
            execution?.runs,
            executionId,
            selectedRun,
            selectedRuns,
            setSelectedRun,
            onSelectRun,
            sortedRuns,
            projectSlug,
            suiteSlug,
        ],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const getName = (storyName: string, storyArchived: boolean, storyParentStoryId: number | null) => {
        if (storyArchived) {
            const suffix = storyParentStoryId ? '(previous version)' : '(archived)';
            return (
                <>
                    {storyName} <span className="opacity-50">{suffix}</span>
                </>
            );
        }
        return storyName;
    };

    const renderRun = (run: RunExecution, statusIcon: ReactElement, runIsSelected: boolean | undefined) => {
        const actions = (
            <>
                {['running'].includes(run.status) ? (
                    <>
                        {canOperate && runIsSelected && socketCancelRun && (
                            <Button
                                variant="destructive"
                                className="text-xs whitespace-nowrap border h-[28px] gap-[8px] px-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    socketCancelRun();
                                }}
                            >
                                Cancel run
                            </Button>
                        )}
                    </>
                ) : null}
            </>
        );

        return (
            <div
                key={run.id}
                className={cn(
                    'flex flex-row w-full items-center px-[16px] py-[8px] bg-white transition-colors duration-500 rounded-[6px]',
                    runIsSelected && 'bg-slate-50',
                )}
            >
                <button
                    onClick={(e) => {
                        if (onSelectRun) {
                            onSelectRun(run, e);
                        } else if (setSelectedRun) {
                            setSelectedRun({ runId: run.id, executionId: parseInt(executionId!) });
                        }
                    }}
                    onDoubleClick={() => {
                        openStepViewer?.();
                    }}
                    className="flex items-start gap-[16px] min-w-0 w-full text-left font-bold font-[500] text-slate-700 outline-0"
                >
                    {statusIcon}
                    <span className="flex flex-col w-full min-w-0">
                        <div className="text-slate-700" title={run.storyName}>
                            {((run.storyMaintenance && run.output === 'DISMISSED') ||
                                (run.storyMaintenance &&
                                    run.storyMaintenanceDate &&
                                    // check that execution.createdAt is newer than run.storyMaintenanceDate
                                    new Date(execution.createdAt).getTime() >
                                        new Date(run.storyMaintenanceDate).getTime())) && (
                                <span className="text-slate-500">In repair: </span>
                            )}
                            {run.storyDraft && <span className="text-slate-500">Draft: </span>}
                            {getName(run.storyName, run.storyArchived, run.parentStoryId)}
                        </div>
                        {run.feedback && (
                            <div className="text-slate-500 font-normal line-clamp-3 [word-break:break-word]">
                                {run.feedback}
                            </div>
                        )}
                        <div className="flex items-center gap-2 justify-end">
                            {actions}
                            {runIsSelected && (
                                <Button
                                    variant="ghost"
                                    className="text-xs whitespace-nowrap h-[28px] gap-[2px] bg-white border px-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openStepViewer?.();
                                    }}
                                >
                                    View steps
                                    <ArrowRight className="text-slate-400" size={16} />
                                </Button>
                            )}
                        </div>
                    </span>
                </button>
            </div>
        );
    };

    return (
        <div className="flex flex-col text-body text-slate-500">
            {/* Enabled runs */}
            {sortedRuns
                .filter((run) => run.storyEnabled !== false)
                .map((run) => {
                    const isSelected =
                        selectedRuns.some((item) => item.runId === run.id) ||
                        (selectedRun && run.id === selectedRun.runId);

                    return (
                        <ElasticHeight key={run.id} id={`run-${run.id}`}>
                            {renderRun(
                                run,
                                <StatusIcon
                                    result={run.result}
                                    status={run.status}
                                    output={run.output}
                                    maintenance={run.storyMaintenance}
                                    disabled={run.storyEnabled === false}
                                    size={24}
                                    spinnerSize={'[24px]'}
                                />,
                                isSelected,
                            )}
                        </ElasticHeight>
                    );
                })}

            {/* Disabled runs section */}
            {sortedRuns.filter((run) => run.storyEnabled === false).length > 0 && (
                <>
                    <div className="text-large text-[hsla(62,42%,14%,1)]">Disabled stories</div>
                    {sortedRuns
                        .filter((run) => run.storyEnabled === false)
                        .map((run) => {
                            const isSelected =
                                selectedRuns.some((item) => item.runId === run.id) ||
                                (selectedRun && run.id === selectedRun.runId);

                            return (
                                <ElasticHeight key={run.id} id={`run-${run.id}`}>
                                    {renderRun(
                                        run,
                                        <StatusIcon
                                            result={run.result}
                                            status={run.status}
                                            output={run.output}
                                            maintenance={run.storyMaintenance}
                                            disabled={run.storyEnabled === false}
                                            size={24}
                                            spinnerSize={'[24px]'}
                                        />,
                                        isSelected,
                                    )}
                                </ElasticHeight>
                            );
                        })}
                </>
            )}
        </div>
    );
};

export default ExecutionCardDetails;

const linearInterpolation = (x: number) => x;

interface ElasticHeightProps {
    id?: string;
    className?: string;
    containerClassName?: string;
    role?: string;
}

export const ElasticHeight: FunctionComponent<PropsWithChildren<ElasticHeightProps>> = ({
    children,
    id,
    className,
    role,
    containerClassName,
}) => {
    const ref = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement>;
    const { height } = useResize({ container: ref });
    const isInitialMount = useRef(true);

    const [styles, api] = useSpring(() => ({
        from: {
            height: height.to(linearInterpolation),
        },
        immediate: true,
    }));

    useEffect(() => {
        api.start({
            to: {
                height: height.to(linearInterpolation),
            },
            immediate: isInitialMount.current,
            config: {
                tension: 170,
                friction: 26,
            },
        });

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }
    }, [api, height]);

    return (
        <animated.div role={role} className={cn('overflow-hidden', containerClassName)} style={{ ...styles }} id={id}>
            <div className={className} ref={ref}>
                {children}
            </div>
        </animated.div>
    );
};
